/**
 * Shape Layer Renderer
 * Renders shape layers (rectangles, circles, lines, polygons)
 */

import React, { useId } from 'react';
import type { ShapeLayer } from '@/types/layers';
import type { ResolvedPosition } from '@/lib/position/resolver';
import type { Brush, GradientStop } from '@/types/brush';

// Generate SVG gradient definitions for a brush
function useBrushDefs(brush: Brush | undefined, id: string): { fill: string; defs: React.ReactNode } {
  if (!brush || brush.type === 'transparent') {
    return { fill: 'none', defs: null };
  }

  if (brush.type === 'solid') {
    return { fill: brush.color, defs: null };
  }

  if (brush.type === 'linear-gradient') {
    const gradId = `lg-${id}`;
    // Convert angle to SVG coordinates
    const angle = brush.angle ?? 180;
    const rad = (angle - 90) * (Math.PI / 180);
    const x1 = 50 + 50 * Math.cos(rad + Math.PI);
    const y1 = 50 + 50 * Math.sin(rad + Math.PI);
    const x2 = 50 + 50 * Math.cos(rad);
    const y2 = 50 + 50 * Math.sin(rad);

    const defs = (
      <defs>
        <linearGradient id={gradId} x1={`${x1}%`} y1={`${y1}%`} x2={`${x2}%`} y2={`${y2}%`}>
          {brush.stops.map((stop: GradientStop, i: number) => (
            <stop key={i} offset={`${stop.position}%`} stopColor={stop.color} />
          ))}
        </linearGradient>
      </defs>
    );
    return { fill: `url(#${gradId})`, defs };
  }

  if (brush.type === 'radial-gradient') {
    const gradId = `rg-${id}`;
    const defs = (
      <defs>
        <radialGradient id={gradId} cx={`${brush.centerX}%`} cy={`${brush.centerY}%`} r="50%">
          {brush.stops.map((stop: GradientStop, i: number) => (
            <stop key={i} offset={`${stop.position}%`} stopColor={stop.color} />
          ))}
        </radialGradient>
      </defs>
    );
    return { fill: `url(#${gradId})`, defs };
  }

  // SVG doesn't natively support conic/sweep gradients - fall back to first color
  if (brush.type === 'sweep-gradient') {
    return { fill: brush.stops[0]?.color ?? 'none', defs: null };
  }

  return { fill: 'none', defs: null };
}

// Simple brush to string for strokes (gradients not supported on strokes currently)
function brushToStroke(brush: Brush | undefined): string {
  if (!brush || brush.type === 'transparent') return 'none';
  if (brush.type === 'solid') return brush.color;
  // For gradients, fall back to first stop color
  if (brush.type === 'linear-gradient' || brush.type === 'radial-gradient' || brush.type === 'sweep-gradient') {
    return brush.stops[0]?.color ?? '#000000';
  }
  return 'none';
}

interface ShapeLayerRendererProps {
  layer: ShapeLayer;
  position: ResolvedPosition;
  isSelected?: boolean;
  isHovered?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  onMouseDown?: (e: React.MouseEvent) => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

export function ShapeLayerRenderer({
  layer,
  position,
  isSelected,
  isHovered,
  onClick,
  onMouseDown,
  onMouseEnter,
  onMouseLeave,
}: ShapeLayerRendererProps) {
  const { content, transform, opacity, width, height } = layer;
  const { shapeType, fill, stroke, strokeWidth, borderRadius, points } = content;

  const resolvedWidth = position.width ?? (typeof width === 'number' ? width : 100);
  const resolvedHeight = position.height ?? (typeof height === 'number' ? height : 100);

  const containerStyle: React.CSSProperties = {
    position: 'absolute',
    left: position.x,
    top: position.y,
    width: resolvedWidth,
    height: resolvedHeight,
    opacity,
    transform: transform
      ? `rotate(${transform.rotation ?? 0}deg) scale(${transform.scaleX ?? 1}, ${transform.scaleY ?? 1})`
      : undefined,
    transformOrigin: transform
      ? `${(transform.originX ?? 0.5) * 100}% ${(transform.originY ?? 0.5) * 100}%`
      : undefined,
    // Hover styling (selection handled by SelectionOverlay)
    outline: isHovered && !isSelected ? '1px dashed #3B82F6' : 'none',
    outlineOffset: 2,
    cursor: 'pointer',
    pointerEvents: layer.locked ? 'none' : 'auto',
  };

  // Use unique ID for gradient definitions
  const uniqueId = useId();
  const { fill: fillValue, defs: fillDefs } = useBrushDefs(fill, `fill-${uniqueId}`);
  const strokeValue = brushToStroke(stroke);

  const renderShape = () => {
    const commonSvgProps = {
      fill: fillValue,
      stroke: strokeValue,
      strokeWidth: strokeWidth ?? 0,
    };

    switch (shapeType) {
      case 'rectangle':
        return (
          <svg width="100%" height="100%" viewBox={`0 0 ${resolvedWidth} ${resolvedHeight}`}>
            {fillDefs}
            <rect
              x={strokeWidth ? strokeWidth / 2 : 0}
              y={strokeWidth ? strokeWidth / 2 : 0}
              width={resolvedWidth - (strokeWidth ?? 0)}
              height={resolvedHeight - (strokeWidth ?? 0)}
              rx={borderRadius ?? 0}
              {...commonSvgProps}
            />
          </svg>
        );

      case 'circle':
        const radius = Math.min(resolvedWidth, resolvedHeight) / 2 - (strokeWidth ?? 0) / 2;
        return (
          <svg width="100%" height="100%" viewBox={`0 0 ${resolvedWidth} ${resolvedHeight}`}>
            {fillDefs}
            <circle
              cx={resolvedWidth / 2}
              cy={resolvedHeight / 2}
              r={radius}
              {...commonSvgProps}
            />
          </svg>
        );

      case 'ellipse':
        return (
          <svg width="100%" height="100%" viewBox={`0 0 ${resolvedWidth} ${resolvedHeight}`}>
            {fillDefs}
            <ellipse
              cx={resolvedWidth / 2}
              cy={resolvedHeight / 2}
              rx={resolvedWidth / 2 - (strokeWidth ?? 0) / 2}
              ry={resolvedHeight / 2 - (strokeWidth ?? 0) / 2}
              {...commonSvgProps}
            />
          </svg>
        );

      case 'line':
        return (
          <svg width="100%" height="100%" viewBox={`0 0 ${resolvedWidth} ${resolvedHeight}`}>
            <line
              x1={0}
              y1={resolvedHeight / 2}
              x2={resolvedWidth}
              y2={resolvedHeight / 2}
              stroke={strokeValue || '#FFFFFF'}
              strokeWidth={strokeWidth ?? 2}
            />
          </svg>
        );

      case 'polygon':
        const sides = points ?? 3;
        const cx = resolvedWidth / 2;
        const cy = resolvedHeight / 2;
        const r = Math.min(cx, cy) - (strokeWidth ?? 0) / 2;
        const polygonPoints = Array.from({ length: sides }, (_, i) => {
          const angle = (i * 2 * Math.PI) / sides - Math.PI / 2;
          return `${cx + r * Math.cos(angle)},${cy + r * Math.sin(angle)}`;
        }).join(' ');

        return (
          <svg width="100%" height="100%" viewBox={`0 0 ${resolvedWidth} ${resolvedHeight}`}>
            {fillDefs}
            <polygon points={polygonPoints} {...commonSvgProps} />
          </svg>
        );

      case 'triangle': {
        const triCx = resolvedWidth / 2;
        const triCy = resolvedHeight / 2;
        const triR = Math.min(triCx, triCy) - (strokeWidth ?? 0) / 2;
        const triPoints = Array.from({ length: 3 }, (_, i) => {
          const angle = (i * 2 * Math.PI) / 3 - Math.PI / 2;
          return `${triCx + triR * Math.cos(angle)},${triCy + triR * Math.sin(angle)}`;
        }).join(' ');

        return (
          <svg width="100%" height="100%" viewBox={`0 0 ${resolvedWidth} ${resolvedHeight}`}>
            {fillDefs}
            <polygon points={triPoints} {...commonSvgProps} />
          </svg>
        );
      }

      case 'pentagon': {
        const pentCx = resolvedWidth / 2;
        const pentCy = resolvedHeight / 2;
        const pentR = Math.min(pentCx, pentCy) - (strokeWidth ?? 0) / 2;
        const pentPoints = Array.from({ length: 5 }, (_, i) => {
          const angle = (i * 2 * Math.PI) / 5 - Math.PI / 2;
          return `${pentCx + pentR * Math.cos(angle)},${pentCy + pentR * Math.sin(angle)}`;
        }).join(' ');

        return (
          <svg width="100%" height="100%" viewBox={`0 0 ${resolvedWidth} ${resolvedHeight}`}>
            {fillDefs}
            <polygon points={pentPoints} {...commonSvgProps} />
          </svg>
        );
      }

      case 'hexagon': {
        const hexCx = resolvedWidth / 2;
        const hexCy = resolvedHeight / 2;
        const hexR = Math.min(hexCx, hexCy) - (strokeWidth ?? 0) / 2;
        const hexPoints = Array.from({ length: 6 }, (_, i) => {
          const angle = (i * 2 * Math.PI) / 6 - Math.PI / 2;
          return `${hexCx + hexR * Math.cos(angle)},${hexCy + hexR * Math.sin(angle)}`;
        }).join(' ');

        return (
          <svg width="100%" height="100%" viewBox={`0 0 ${resolvedWidth} ${resolvedHeight}`}>
            {fillDefs}
            <polygon points={hexPoints} {...commonSvgProps} />
          </svg>
        );
      }

      case 'star': {
        const starCx = resolvedWidth / 2;
        const starCy = resolvedHeight / 2;
        const outerR = Math.min(starCx, starCy) - (strokeWidth ?? 0) / 2;
        const innerR = outerR * 0.4; // Inner radius is 40% of outer
        const starPoints = Array.from({ length: 10 }, (_, i) => {
          const angle = (i * Math.PI) / 5 - Math.PI / 2;
          const r = i % 2 === 0 ? outerR : innerR;
          return `${starCx + r * Math.cos(angle)},${starCy + r * Math.sin(angle)}`;
        }).join(' ');

        return (
          <svg width="100%" height="100%" viewBox={`0 0 ${resolvedWidth} ${resolvedHeight}`}>
            {fillDefs}
            <polygon points={starPoints} {...commonSvgProps} />
          </svg>
        );
      }

      default:
        return null;
    }
  };

  return (
    <div
      style={containerStyle}
      onClick={onClick}
      onMouseDown={onMouseDown}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      data-layer-id={layer.id}
    >
      {renderShape()}
    </div>
  );
}
